
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { usePermissions } from '@/hooks/use-permissions';
import { CategoryDialog } from './CategoryDialog';
import { CategoryTable } from './CategoryTable';
import { useCategories } from './hooks/useCategories';
import { Category } from './types';
import { useIsMobile } from '@/hooks/use-mobile';

const CategoryManagement = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<Partial<Category> | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { isSuperAdmin } = usePermissions();
  const { categories, isLoading, fetchCategories, handleDeleteCategory } = useCategories();
  const isMobile = useIsMobile();

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setCurrentCategory({ ...category });
      setIsEditing(true);
    } else {
      setCurrentCategory({ name: '', slug: '', description: '', active: true });
      setIsEditing(false);
    }
    setDialogOpen(true);
  };

  // Only super admins can manage categories to prevent inconsistencies
  if (!isSuperAdmin()) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Category Management</CardTitle>
          <CardDescription>
            Only super administrators can manage categories
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className={`flex ${isMobile ? 'flex-col' : 'flex-row'} items-center justify-between gap-4`}>
        <div>
          <CardTitle>Category Management</CardTitle>
          <CardDescription>
            Manage categories for insight cards and polls
          </CardDescription>
        </div>
        <Button onClick={() => handleOpenDialog()} className={isMobile ? "w-full" : ""}>
          <Plus size={16} className="mr-2" />
          Add Category
        </Button>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {isLoading ? (
          <div className="text-center py-8">Loading categories...</div>
        ) : (
          <CategoryTable 
            categories={categories}
            onEdit={handleOpenDialog}
            onDelete={handleDeleteCategory}
            isMobile={isMobile}
          />
        )}
      </CardContent>

      <CategoryDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        currentCategory={currentCategory}
        isEditing={isEditing}
        onSuccess={fetchCategories}
      />
    </Card>
  );
};

export default CategoryManagement;
